import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  Button,
  Pagination,
  useTheme,
  alpha,
} from "@mui/material";
import { useEffect, useState, useMemo, useRef } from "react";
import StoreIcon from "@mui/icons-material/Store";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockIcon from "@mui/icons-material/Lock";

import {
  setupManagementUrl,
  inventoryManagementUrl,
  companyManagementUrl,
} from "../../../config";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import {
  fetchDataEngine,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";
import { tokens } from "../../../theme";
import {
  AllInventoryViews,
  retailInventoryQuery,
  allProductsViews,
  inventoryQuery,
  SearchRetailInventory,
  inventoryStatsQuery,
  allCompanyBranchesView,
} from "../../../data/Axios/queries";
import BranchInventoryForm from "./BranchInventoryForm";

const base_url = inventoryManagementUrl.uri;
const company_baseurl = companyManagementUrl.uri;

const actions = {
  entity: "Branch Inventory",
  edit: {
    key: "EDIT",
    button_name: "Edit",
    Show_Button: true,
    permission: "EditPermission",
  },
  add: {
    key: "ADD",
    button_name: "Add",
    Show_Button: false,
    permission: "CreatePermission",
  },
  activateDeactivate: {
    key: "ACTIVATEDEACTIVATE",
    button_name: "Deactivate",
    Show_Button: false,
    permission: "ActivatePermission",
    modelName: "inventoryIds",
    inputObjectName: "bulkActivateDeactivateInventory",
  },
  AssignTask: {
    key: "ASSIGN_TASK",
    button_name: "Assign Task",
    Show_Button: false,
    permission: "hasNoPermission",
    roleGroupId: [1, 2],
  },
  showMore: {
    key: "SHOWMORE",
    button_name: "Show More",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
    title: "Inventory Details",
  },
  ShowRetailDistributionDetails: {
    key: "SHOW_DISTRIBUTION_DETAILS",
    button_name: "View Distribution",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
  },
};

function InventoryTable() {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const branchId = parseInt(decodedToken.BranchId);

  // ✅ Determine if user is branch-locked (branchId is a valid non-zero number)
  const isBranchLocked = !isNaN(branchId) && branchId > 0;

  const [inventory, setInventory] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  // Filter states
  const [branches, setBranches] = useState([]);

  // ✅ If branch-locked, default selectedBranch to branchId from token
  const [selectedBranch, setSelectedBranch] = useState(
    isBranchLocked ? branchId : ""
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [distributionFilter, setDistributionFilter] = useState("all");

  // Guard against overlapping requests
  const fetchInProgress = useRef(false);

  // Statistics
  const [stats, setStats] = useState({
    totalPairs: 0,
    totalProducts: 0,
    averagePrice: 0,
    activeItems: 0,
    distributedItems: 0,
    notDistributedItems: 0,
    expectedProfit: 0,
    totalSellingValue: 0,
    totalBuyingValue: 0,
  });

  // Theme colors
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isDark = muiTheme.palette.mode === "dark";

  const theme = {
    primary: isDark ? colors.blueAccent[400] : "#1976D2",
    secondary: isDark ? colors.blueAccent[500] : "#0288D1",
    success: "#4CAF50",
    warning: "#FF9800",
    error: "#F44336",
    lightBg: isDark ? alpha(colors.blueAccent[400], 0.15) : "#E3F2FD",
    cardBg: isDark ? colors.primary[400] : "#FFFFFF",
    pageBg: isDark ? colors.primary[500] : "#F5F7FA",
    lockedBg: isDark ? alpha(colors.blueAccent[400], 0.08) : "#F0F7FF",
  };

  // ✅ Fetch branches for dropdown (only needed if NOT branch-locked)
  const fetchBranches = async () => {
    try {
      const response = await graphqlQuery(
        allCompanyBranchesView,
        company_baseurl
      );
      setBranches(response || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
  };

  // ✅ Fetch server-side statistics
  const fetchServerStats = async () => {
    try {
      setStatsLoading(true);
      const statsParams = {
        branchId: selectedBranch || null,
        status: statusFilter,
        distribution: distributionFilter,
        search: "",
      };

      const response = await graphqlQuery(
        inventoryStatsQuery(statsParams),
        base_url
      );

      if (response && response.length > 0) {
        setStats({
          totalPairs: response[0].totalPairs || 0,
          totalProducts: response[0].totalProducts || 0,
          averagePrice: response[0].averagePrice || 0,
          activeItems: response[0].activeItems || 0,
          distributedItems: response[0].distributedItems || 0,
          notDistributedItems: response[0].notDistributedItems || 0,
          expectedProfit: response[0].expectedProfit || 0,
          totalSellingValue: response[0].totalSellingValue || 0,
          totalBuyingValue: response[0].totalBuyingValue || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  // ✅ Load inventory data with filters and paging
  const loadInventory = async () => {
    if (fetchInProgress.current) {
      console.log("inventory fetch already in progress, skipping");
      return;
    }
    setLoading(true);
    setError("");
    fetchInProgress.current = true;

    try {
      const offset = page * pageSize;
      const params = {
        searchTerm: searchQuery.trim() || "",
        limit: pageSize,
        offset,
      };

      const result = await graphqlQuery(
        SearchRetailInventory(params),
        base_url
      );

      let rows = Array.isArray(result?.items) ? result.items : [];
      const totalCount = result?.totalCount ?? 0;

      // Apply client-side filters
      if (selectedBranch) {
        rows = rows.filter(
          (r) => String(r.branchId) === String(selectedBranch)
        );
      }
      if (statusFilter !== "all") {
        const isActive = statusFilter === "active" || statusFilter === "1";
        rows = rows.filter((r) => Boolean(r.isActive) === isActive);
      }
      if (distributionFilter !== "all") {
        const isDist =
          distributionFilter === "distributed" || distributionFilter === "1";
        rows = rows.filter((r) => Boolean(r.isDistributed) === isDist);
      }

      if (rows.length > 0) {
        const inventoryData = rows.map((item) => ({
          ...item,
          stockDueDate: item.stockDueDate
            ? new Date(item.stockDueDate).toLocaleDateString()
            : "N/A",
          averageBuyingPrice: item.averageBuyingPrice || item.averageBP || 0,
          averageSellingPrice:
            item.averageSellingPrice || item.averageSP || 0,
          profitPerUnit:
            (item.averageSellingPrice || item.averageSP || 0) -
            (item.averageBuyingPrice || item.averageBP || 0),
          totalProfit:
            ((item.averageSellingPrice || item.averageSP || 0) -
              (item.averageBuyingPrice || item.averageBP || 0)) *
            (item.quantity || 0),
        }));

        setInventory(inventoryData);
        setRowCount(totalCount);

        const total = inventoryData.reduce(
          (sum, item) => sum + (item.totalValue ?? 0),
          0
        );
        setTotalAmount(
          total.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        );
      } else {
        setInventory([]);
        setRowCount(0);
        setTotalAmount(0);
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
      setError("Failed to load inventory data. Please try again.");
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  };

  // ✅ Whenever filters, search, or pageSize change, reset page and reload
  useEffect(() => {
    setPage(0);
    fetchServerStats();
    loadInventory();
  }, [selectedBranch, statusFilter, distributionFilter, searchQuery, pageSize]);

  // ✅ Reload when page number changes
  useEffect(() => {
    loadInventory();
  }, [page]);

  // ✅ Handle refresh
  const handleRefresh = () => {
    fetchServerStats();
    loadInventory();
  };

  // ✅ Clear all filters (respects branch lock)
  const handleClearFilters = () => {
    // Only reset branch if user is NOT locked to a branch
    if (!isBranchLocked) {
      setSelectedBranch("");
    }
    setSearchQuery("");
    setStatusFilter("all");
    setDistributionFilter("all");
    setPage(0);
  };

  // ✅ Check if filters are active (branch filter excluded if locked)
  const hasActiveFilters =
    (!isBranchLocked && selectedBranch) ||
    searchQuery ||
    statusFilter !== "all" ||
    distributionFilter !== "all";

  // ✅ Load data on mount
  useEffect(() => {
    fetchBranches();
    loadInventory();
    fetchServerStats();
  }, []);

  // ✅ Helper: get current branch display name
  const currentBranchName =
    isBranchLocked
      ? `${branches.find((b) => b.id === parseInt(branchId)) ?.branchName }`
      : branches.find((b) => b.id === selectedBranch )?.branchName || "Unknown";

  return (
    <Box sx={{ p: 3, bgcolor: theme.pageBg, minHeight: "100vh" }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <StoreIcon sx={{ fontSize: 32, color: theme.primary, mr: 1 }} />
          <Typography variant="h4" fontWeight="700" color={theme.primary}>
            Retail Inventory Management
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {isBranchLocked
            ? `Showing inventory for your branch: ${currentBranchName}`
            : "Monitor and manage retail inventory across all branches"}
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Total Value Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: isDark
                ? `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
                : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
              color: "white",
              border: isDark
                ? `1px solid ${alpha(colors.blueAccent[400], 0.4)}`
                : "none",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                width: "150px",
                height: "150px",
                background: "rgba(255, 255, 255, 0.07)",
                borderRadius: "50%",
                top: "-50px",
                right: "-50px",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AttachMoneyIcon sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Total Value
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="700">
                KSh{" "}
                {stats.totalSellingValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                {isBranchLocked
                  ? currentBranchName
                  : hasActiveFilters
                  ? "Filtered View"
                  : "All Branches"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Pairs Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Total Pairs
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    color={theme.primary}
                  >
                    {stats.totalPairs.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.totalProducts} products
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: theme.lightBg, p: 1.5, borderRadius: 2 }}>
                  <InventoryIcon
                    sx={{ color: theme.primary, fontSize: 28 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Expected Profit Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Expected Profit
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    color={theme.success}
                  >
                    KSh{" "}
                    {stats.expectedProfit.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Based on Avg BP & SP
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: "#E8F5E9", p: 1.5, borderRadius: 2 }}>
                  <AccountBalanceWalletIcon
                    sx={{ color: theme.success, fontSize: 28 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Distribution Status Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Distribution Status
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight="700"
                        color={theme.success}
                      >
                        {stats.distributedItems}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Distributed
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight="700"
                        color={theme.warning}
                      >
                        {stats.notDistributedItems}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: "#FFF3E0", p: 1.5, borderRadius: 2 }}>
                  <LocalShippingIcon
                    sx={{ color: theme.warning, fontSize: 28 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Actions Section */}
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: theme.cardBg }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon sx={{ color: theme.primary }} />
          <Typography variant="h6" fontWeight="600">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${
                [
                  !isBranchLocked && selectedBranch,
                  searchQuery,
                  statusFilter !== "all",
                  distributionFilter !== "all",
                ].filter(Boolean).length
              } Active`}
              size="small"
              color="primary"
              onDelete={handleClearFilters}
            />
          )}
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search any field (product, code, cartons, price, branch, etc)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Branch Filter — hidden when user is locked to a branch */}
          {isBranchLocked ? (
            // ✅ Show a read-only locked branch indicator instead of the dropdown
            <Grid item xs={12} sm={6} md={2.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  border: `1px solid`,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: theme.lockedBg,
                  height: 40,
                }}
              >
                <LockIcon sx={{ fontSize: 16, color: theme.primary }} />
                <StoreIcon sx={{ fontSize: 16, color: theme.primary }} />
                <Typography
                  variant="body2"
                  color={theme.primary}
                  fontWeight="600"
                  noWrap
                >
                  {currentBranchName}
                </Typography>
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={selectedBranch}
                  label="Branch"
                  onChange={(e) => setSelectedBranch(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Branches</em>
                  </MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <StoreIcon
                          sx={{ fontSize: 18, color: theme.primary }}
                        />
                        {branch.branchName}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Status Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon
                      sx={{ fontSize: 18, color: theme.success }}
                    />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: theme.error,
                      }}
                    />
                    Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Distribution Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Distribution</InputLabel>
              <Select
                value={distributionFilter}
                label="Distribution"
                onChange={(e) => setDistributionFilter(e.target.value)}
              >
                <MenuItem value="all">All Items</MenuItem>
                <MenuItem value="distributed">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalShippingIcon
                      sx={{ fontSize: 18, color: theme.success }}
                    />
                    Distributed
                  </Box>
                </MenuItem>
                <MenuItem value="not-distributed">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: theme.warning,
                      }}
                    />
                    Not Distributed
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Actions */}
          <Grid item xs={12} md={1.5}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    bgcolor: theme.lightBg,
                    "&:hover": { bgcolor: theme.primary, color: "white" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/* Only show branch chip if user is NOT locked */}
            {!isBranchLocked && selectedBranch && (
              <Chip
                label={`Branch: ${currentBranchName}`}
                size="small"
                onDelete={() => setSelectedBranch("")}
                color="primary"
                variant="outlined"
              />
            )}
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery("")}
                color="primary"
                variant="outlined"
              />
            )}
            {statusFilter !== "all" && (
              <Chip
                label={`Status: ${
                  statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)
                }`}
                size="small"
                onDelete={() => setStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {distributionFilter !== "all" && (
              <Chip
                label={`Distribution: ${
                  distributionFilter === "distributed"
                    ? "Distributed"
                    : "Not Distributed"
                }`}
                size="small"
                onDelete={() => setDistributionFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Dynamic Table */}
      <Paper
        elevation={0}
        sx={{ borderRadius: 2, overflow: "hidden", bgcolor: theme.cardBg }}
      >
        {loading && inventory.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            title="Inventory Items"
            subtitle={`Showing ${inventory.length} record${
              inventory.length === 1 ? "" : "s"
            }${hasActiveFilters ? " (filtered)" : ""}`}
            query={inventoryQuery}
            FormComponent={BranchInventoryForm}
            base_url={base_url}
            actions={actions}
            rows={inventory}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            paginationMode="server"
            pagination={true}
            onServerAction={handleRefresh}
          />
        )}
      </Paper>
    </Box>
  );
}

export default InventoryTable;